<template>
  <div
    class="flex bg-gray-100 p-1.5 rounded-full w-full sm:w-auto overflow-x-auto no-scrollbar"
  >
    <button
      v-for="diningOption in diningOptions"
      :key="diningOption.id"
      @click="handleDiningOptionClick(diningOption)"
      :class="[
        'flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all whitespace-nowrap',
        isDiningOptionActive(diningOption.id)
          ? 'bg-white text-primary-600 shadow-sm ring-1 ring-black/5'
          : 'text-gray-500 hover:text-gray-700'
      ]"
    >
      <component :is="getIconForDiningOption(diningOption.name)" :size="16" />
      {{ diningOption.name }}
    </button>

    <MinimumOrderModal
      :is-open="isMinimumModalOpen"
      :minimum-amount="currentMinimumAmount"
      :currency-symbol="currency"
      @close="isMinimumModalOpen = false"
      @navigate-to-order="handleNavigateToOrder"
      @cancel="isMinimumModalOpen = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { Store, Car, Bike } from "lucide-vue";
import {
  saveDiningOption,
  saveOrderConfig
} from "@/services/localStorageService";
import { useDiningOptions } from "@/composables/useDiningOptions";
import { useSelectedStore } from "@/composables/useSelectedStore";
import MinimumOrderModal from "./MinimumOrderModal.vue";

export default {
  name: "OrderTypeToggle",
  components: {
    Store,
    Car,
    Bike,
    MinimumOrderModal
  },
  props: {
    grandTotal: {
      type: Number,
      default: 0
    },
    alertUser: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMinimumModalOpen: false,
      currentMinimumAmount: 0,
      pendingDiningOption: null
    };
  },
  computed: {
    ...mapGetters([
      "availableDiningOptions",
      "orderConfig",
      "getCurrencySymbol"
    ]),
    diningOptions() {
      // Use useSelectedStore to get filtered dining options based on current time
      if (this._selectedStore) {
        return this._selectedStore.getAvailableDiningOptions();
      }
      // Fallback to all available dining options if composable not initialized
      return this.availableDiningOptions || [];
    },
    config() {
      return this.orderConfig;
    },
    currency() {
      return this.getCurrencySymbol || "$";
    }
  },
  created() {
    this._diningOptionsComposable = useDiningOptions(this.$store);
    this._selectedStore = useSelectedStore(this.$store);
  },
  methods: {
    ...mapMutations(["SET_ORDER_CONFIG"]),

    setDiningOptionAndConfig(diningOption) {
      this.$store.commit("store/setDiningOption", diningOption.id);

      const scheduleEnabled = diningOption?.is_scheduling_enabled === true;
      const orderType = this.getOrderTypeFromDiningOption(diningOption.name);

      const newConfig = {
        type: orderType,
        timing:
          !scheduleEnabled && this.config.timing === "scheduled"
            ? "asap"
            : this.config.timing || "asap",
        scheduledDay:
          !scheduleEnabled && this.config.timing === "scheduled"
            ? undefined
            : this.config.scheduledDay || undefined,
        scheduledTime:
          !scheduleEnabled && this.config.timing === "scheduled"
            ? undefined
            : this.config.scheduledTime || undefined
      };

      this.SET_ORDER_CONFIG(newConfig);
      saveDiningOption(diningOption.id);
      saveOrderConfig(newConfig);
    },

    handleNavigateToOrder() {
      if (this.pendingDiningOption) {
        this.setDiningOptionAndConfig(this.pendingDiningOption);
      }
      this.$router.back();
      this.isMinimumModalOpen = false;
      this.pendingDiningOption = null;
    },

    getOrderTypeFromDiningOption(name) {
      const nameLower = (name || "").toLowerCase();
      if (nameLower.includes("pick")) return "pickup";
      if (nameLower.includes("curb")) return "curbside";
      if (nameLower.includes("deliver")) return "delivery";
      if (nameLower.includes("dine")) return "dinein";
      return "pickup";
    },

    handleDiningOptionClick(diningOption) {
      const minAmount = diningOption.minimum_order_requirements || 0;

      if (minAmount > 0 && this.grandTotal < minAmount && this.alertUser) {
        this.pendingDiningOption = diningOption;
        this.currentMinimumAmount = minAmount;
        this.isMinimumModalOpen = true;
        return;
      }

      this.setDiningOptionAndConfig(diningOption);
      this.pendingDiningOption = null;
    },

    isDiningOptionActive(diningOptionId) {
      return this.$store.getters.dining_option === diningOptionId;
    },
    getIconForDiningOption(name) {
      const nameLower = (name || "").toLowerCase();
      if (nameLower.includes("pick")) return "Store";
      if (nameLower.includes("curb")) return "Car";
      if (nameLower.includes("deliver")) return "Bike";
      if (nameLower.includes("dine")) return "Store";
      return "Store";
    }
  }
};
</script>

<style></style>
